import { useQueries } from '@tanstack/react-query'
import {
  v2AdminListActivityResearchOptions,
  v2AdminListInvestorUpdateProposalsOptions,
  v2AdminListMatcherJobsOptions,
  v2AdminListOutreachReplyClassificationOptions,
  v2AdminListPersonaResearchOptions,
} from '@workspace/api-backend/react-query'
import {
  adminCallCandidatesQueryKey,
  adminLimitsPendingQueryKey,
  outreachApi,
} from '../../outreach/api'

export interface AdminPendingCounts {
  outreach?: number
  replyClassification?: number
  personaResearch?: number
  activityResearch?: number
  matcherJobs?: number
  investorUpdateProposals?: number
}

const POLL_MS = 30_000
const PENDING_STATUSES = ['PENDING', 'ENQUEUED', 'ACTIVE'] as const
const MATCHER_PENDING_STATUSES = ['PENDING', 'PROCESSING'] as const

type TotalResponse = { data?: { total?: number } }

function sumTotals(results: Array<{ data?: unknown; isError: boolean }>): number | undefined {
  let total = 0
  let anyOk = false
  for (const r of results) {
    if (r.isError) continue
    const data = r.data as TotalResponse | undefined
    if (data?.data?.total != null) {
      anyOk = true
      total += data.data.total
    }
  }
  return anyOk ? total : undefined
}

/**
 * Fans out pending-count queries used to populate admin sidebar badges.
 * SSR-safe: each query independently caches, and errors collapse to `undefined`
 * (no badge), so a single failing endpoint will not break the sidebar.
 */
export function useAdminPendingCounts(): AdminPendingCounts {
  // Reply classification: only PENDING needs admin attention. ENQUEUED /
  // ACTIVE are in-flight by the classifier worker — counting them would make
  // the badge stick at a steady-state number even when nothing is actionable.
  const replyClassificationResults = useQueries({
    queries: [
      {
        ...v2AdminListOutreachReplyClassificationOptions({ query: { page: 1, status: 'PENDING' } }),
        refetchInterval: POLL_MS,
      },
    ],
  })

  const personaResearchResults = useQueries({
    queries: PENDING_STATUSES.map((status) => ({
      ...v2AdminListPersonaResearchOptions({ query: { page: 1, status } }),
      refetchInterval: POLL_MS,
    })),
  })

  const activityResearchResults = useQueries({
    queries: PENDING_STATUSES.map((status) => ({
      ...v2AdminListActivityResearchOptions({ query: { page: 1, status } }),
      refetchInterval: POLL_MS,
    })),
  })

  // Only PENDING is actionable — a decided proposal needs nothing from anyone.
  const investorUpdateProposalResults = useQueries({
    queries: [
      {
        ...v2AdminListInvestorUpdateProposalsOptions({ query: { page: 1, status: 'PENDING' } }),
        refetchInterval: POLL_MS,
      },
    ],
  })

  const matcherJobsResults = useQueries({
    queries: MATCHER_PENDING_STATUSES.map((status) => ({
      ...v2AdminListMatcherJobsOptions({ query: { page: 1, status } }),
      refetchInterval: POLL_MS,
    })),
  })

  const [callCandidatesResult, limitsPendingResult] = useQueries({
    queries: [
      {
        queryKey: adminCallCandidatesQueryKey(),
        queryFn: () => outreachApi.adminCallCandidates(),
        refetchInterval: POLL_MS,
      },
      {
        queryKey: adminLimitsPendingQueryKey(),
        queryFn: () => outreachApi.adminLimitsPending(),
        refetchInterval: POLL_MS,
      },
    ],
  })

  const callCandidates = callCandidatesResult.isError
    ? undefined
    : Array.isArray(callCandidatesResult.data)
      ? callCandidatesResult.data.length
      : undefined
  const limitsPending = limitsPendingResult.isError
    ? undefined
    : Array.isArray(limitsPendingResult.data)
      ? limitsPendingResult.data.length
      : undefined

  // Sum whichever sources resolved; only return a total if at least one did.
  const parts = [callCandidates, limitsPending].filter((n): n is number => typeof n === 'number')
  const outreach = parts.length > 0 ? parts.reduce((a, b) => a + b, 0) : undefined

  return {
    outreach,
    replyClassification: sumTotals(replyClassificationResults),
    personaResearch: sumTotals(personaResearchResults),
    activityResearch: sumTotals(activityResearchResults),
    matcherJobs: sumTotals(matcherJobsResults),
    investorUpdateProposals: sumTotals(investorUpdateProposalResults),
  }
}
